<script>
    /**
     * ConfirmDialog — confirm/cancel flow built on Dialog.
     *
     * Message content is either the `message` prop (plain text, auto-escaped)
     * or the `children` snippet for rich markup — there is intentionally no
     * HTML-string rendering, so user-derived values can be interpolated
     * without escaping concerns.
     *
     * Props:
     *  - title:         heading text
     *  - message:       plain-text body (ignored when children is provided)
     *  - status:        'info' | 'warning' | 'danger' (default 'warning') —
     *                   tints the icon badge and the confirm button
     *  - icon:          material icon override for the badge
     *  - confirmLabel / cancelLabel: button labels
     *  - busy:          disables both buttons and shows a spinner on confirm
     *  - onconfirm / oncancel: action callbacks (oncancel also fires for
     *                   Escape, backdrop click, and the × button)
     *  - children:      rich body snippet
     */
    import Dialog from './Dialog.svelte';

    let {
        title,
        message = null,
        status = 'warning',
        icon = null,
        confirmLabel = 'Confirm',
        cancelLabel = 'Cancel',
        busy = false,
        onconfirm = null,
        oncancel = null,
        children = null,
    } = $props();

    function handleCancel() {
        if (busy) return;
        oncancel?.();
    }

    function handleConfirm() {
        if (busy) return;
        onconfirm?.();
    }
</script>

<Dialog {title} {status} {icon} onclose={handleCancel}>
    {#if children}
        {@render children()}
    {:else if message}
        <p>{message}</p>
    {/if}

    {#snippet footer()}
        <button type="button" class="dlg-btn cancel" disabled={busy} onclick={handleCancel}>
            {cancelLabel}
        </button>
        <button type="button" class="dlg-btn confirm {status}" disabled={busy} onclick={handleConfirm}>
            {#if busy}<span class="spinner" aria-hidden="true"></span>{/if}
            {confirmLabel}
        </button>
    {/snippet}
</Dialog>

<style>
    .dlg-btn {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        height: 34px;
        padding: 0 16px;
        border-radius: 999px;
        border: 1px solid transparent;
        font-family: 'Inter', sans-serif;
        font-size: 12.5px;
        font-weight: 700;
        letter-spacing: 0.02em;
        cursor: pointer;
        transition: background 0.12s, color 0.12s, border-color 0.12s;
    }
    .dlg-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    .dlg-btn.cancel {
        background: transparent;
        border-color: rgba(255, 255, 255, 0.14);
        color: rgba(255, 255, 255, 0.75);
    }
    .dlg-btn.cancel:hover:not(:disabled) {
        background: #2b2b2b;
        color: #fff;
    }

    .dlg-btn.confirm.info {
        background: #4ac6ff;
        color: #04222f;
    }
    .dlg-btn.confirm.info:hover:not(:disabled) {
        background: #6fd2ff;
    }
    .dlg-btn.confirm.warning {
        background: #f2a340;
        color: #1a1200;
    }
    .dlg-btn.confirm.warning:hover:not(:disabled) {
        background: #f5b464;
    }
    .dlg-btn.confirm.danger {
        background: #e5484d;
        color: #fff;
    }
    .dlg-btn.confirm.danger:hover:not(:disabled) {
        background: #ec5f63;
    }

    .spinner {
        width: 13px;
        height: 13px;
        flex: none;
        border-radius: 50%;
        border: 2px solid currentColor;
        border-top-color: transparent;
        animation: dlg-spin 0.7s linear infinite;
    }
    @keyframes dlg-spin {
        to { transform: rotate(360deg); }
    }
</style>
